/**
 * API Client for StockSync Hub Frontend
 * Axios-based client with JWT token interceptor and error handling
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type {
  Product,
  Channel,
  Alert,
  SyncEvent,
  User,
  Tenant,
  ChannelType,
  AlertType,
} from '@/types';

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Construct WebSocket URL with proper protocol (wss for https/production, ws for development)
function getWSBaseURL(): string {
  const configured = import.meta.env.VITE_WS_URL;
  if (configured) {
    return configured;
  }

  // Default to wss:// (secure WebSocket) when served over HTTPS (production)
  // Development can use ws:// only if explicitly configured or run on http://localhost
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

const WS_BASE_URL = getWSBaseURL();

// ============================================================================
// Types
// ============================================================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  tenantName: string;
  tenantSlug: string;
  email: string;
  password: string;
  name?: string;
  promoCode?: string;
}

// Backend user shape (differs from frontend User type)
export interface BackendSafeUser {
  id: string;
  tenantId: string;
  email: string;
  name: string | null;
  role: string;
  onboardingComplete: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
}

export interface AuthResponse {
  user: BackendSafeUser;
  tenant: Tenant;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export interface CreateProductData {
  sku: string;
  name: string;
  currentStock?: number;
  bufferStock?: number;
  metadata?: Record<string, unknown>;
}

export interface UpdateProductData {
  sku?: string;
  name?: string;
  currentStock?: number;
  bufferStock?: number;
  metadata?: Record<string, unknown>;
}

export interface CreateChannelData {
  type: ChannelType;
  name: string;
  credentials?: Record<string, unknown>;
  externalInstanceId?: string;
}

export interface UpdateChannelData {
  name?: string;
  credentials?: Record<string, unknown>;
  isActive?: boolean;
  externalInstanceId?: string;
}

export interface CreateMappingData {
  productId: string;
  channelId: string;
  externalId: string;
  externalSku?: string;
}

export interface CatalogSearchResult {
  externalId: string;
  sku: string;
  name: string;
  price: number | null;
  stock: number | null;
  alreadyMapped: boolean;
}

export interface CatalogSearchResponse {
  results: CatalogSearchResult[];
  totalInCatalog: number;
  snapshotAgeSeconds: number;
}

export interface DashboardStats {
  totalProducts: number;
  syncedToday: number;
  activeChannels: number;
  pendingAlerts: number;
  aiAgentStatus: {
    watcher: string;
    sync: string;
    guardian: string;
    alert: string;
  };
  recentActivity: any[];
  additionalStats?: {
    lowStockProducts: number;
    totalSyncEvents: number;
    failedSyncsToday: number;
    activeConnections: number;
    queueStats?: {
      sync: { waiting: number; active: number };
      webhook: { waiting: number; active: number };
    };
  };
}

export interface AgentStatus {
  name: string;
  state: 'stopped' | 'starting' | 'running' | 'stopping' | 'error';
  lastActivity: string | null;
  processedCount: number;
  errorCount: number;
  error?: string;
}

export interface EngineStatus {
  state: string;
  startedAt: string | null;
  uptime: number;
  agents: {
    watcher: AgentStatus;
    sync: AgentStatus;
    guardian: AgentStatus;
    alert: AgentStatus;
  };
  stats: {
    totalSyncs: number;
    successfulSyncs: number;
    failedSyncs: number;
    productsUpdated: number;
    alertsCreated: number;
    driftsDetected: number;
    driftsRepaired: number;
  };
}

export interface SyncStatus {
  queueStats: {
    sync: { waiting: number; active: number; completed: number; failed: number };
    webhook: { waiting: number; active: number };
  } | null;
  pendingEvents: any[];
  activeJobs: any[];
  timestamp: string;
}

export interface DashboardOverview {
  inventory: {
    totalProducts: number;
    inStock: number;
    outOfStock: number;
    totalValue: number;
  };
  topProducts: Array<{
    id: string;
    name: string;
    sku: string;
    currentStock: number;
    salePrice: number;
  }>;
  lowStockProducts: Array<{
    id: string;
    name: string;
    currentStock: number;
    bufferStock: number;
  }>;
  channels: Array<{
    id: string;
    name: string;
    type: string;
    lastSync: string | null;
    isActive: boolean;
  }>;
  bufferStockSummary: {
    configured: number;
    belowThreshold: number;
  };
}

export interface BootstrapData {
  user: { user: BackendSafeUser; tenant: any } | null;
  overview: DashboardOverview | null;
  agents: any[] | null;
  channels: any[] | null;
  billing: {
    plan: string;
    planStatus: string;
    shopLimit: number;
    trialEndsAt: string | null;
  } | null;
}

export interface BillingStatus {
  plan: string;
  planStatus: string;
  shopLimit: number;
  currentShopCount: number;
  trialEndsAt: string | null;
  canAddShop: boolean;
}

// ============================================================================
// Token Storage
// ============================================================================

const TOKEN_KEY = 'stocksync_token';
const REFRESH_TOKEN_KEY = 'stocksync_refresh_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearStoredRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/** Decode JWT payload and return the exp claim (seconds since epoch), or null if unparsable. */
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Returns true if the token will expire within the given number of seconds. */
function isTokenExpiringSoon(token: string, withinSeconds: number): boolean {
  const exp = getTokenExpiry(token);
  if (exp === null) return false;
  return exp - Date.now() / 1000 < withinSeconds;
}

// Mutex to prevent concurrent refresh attempts
let refreshPromise: Promise<string> | null = null;

// ============================================================================
// Axios Instance
// ============================================================================

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token and proactively refresh if expiring soon
apiClient.interceptors.request.use(
  async (config) => {
    // Skip refresh logic for auth endpoints to avoid loops
    const url = config.url || '';
    const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register');

    let token = getStoredToken();

    // Proactive refresh: if the token will expire within 2 minutes, refresh it now
    if (token && !isAuthEndpoint && isTokenExpiringSoon(token, 120)) {
      const storedRefresh = getStoredRefreshToken();
      if (storedRefresh) {
        try {
          if (!refreshPromise) {
            refreshPromise = authApi.refreshToken(storedRefresh).then((r) => r.accessToken);
          }
          token = await refreshPromise;
        } catch {
          // Refresh failed — continue with the existing token; the 401 handler will catch it
        } finally {
          refreshPromise = null;
        }
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper: clear all auth state and redirect to login
function clearAuthAndRedirect(): void {
  clearStoredToken();
  clearStoredRefreshToken();
  try { localStorage.removeItem('stockclerk-auth'); } catch {}
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// Response interceptor - Handle errors with token refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    if (error.response) {
      const { status, data } = error.response;
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      // Handle 401 Unauthorized — attempt token refresh before giving up
      if (status === 401 && originalRequest && !originalRequest._retry) {
        // Don't retry refresh/login/register endpoints
        const url = originalRequest.url || '';
        const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register');

        if (!isAuthEndpoint) {
          const storedRefresh = getStoredRefreshToken();
          if (storedRefresh) {
            originalRequest._retry = true;
            try {
              if (!refreshPromise) {
                refreshPromise = authApi.refreshToken(storedRefresh).then((r) => r.accessToken);
              }
              const newAccessToken = await refreshPromise;
              refreshPromise = null;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return apiClient(originalRequest);
            } catch {
              refreshPromise = null;
              // Refresh failed — fall through to clear and redirect
            }
          }
        }

        clearAuthAndRedirect();
      }

      // Handle 402 Payment Required (trial expired)
      if (status === 402) {
        if (window.location.pathname !== '/settings') {
          window.location.href = '/settings?tab=billing&expired=true';
        }
      }

      // Create a more informative error
      const message = data?.error || data?.message || 'An error occurred';
      const enhancedError = new Error(message);
      (enhancedError as any).status = status;
      (enhancedError as any).data = data;
      return Promise.reject(enhancedError);
    }

    // Network error
    if (error.request) {
      const networkError = new Error('Network error. Please check your connection.');
      (networkError as any).isNetworkError = true;
      return Promise.reject(networkError);
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/login', credentials);
    if (response.data.data) {
      setStoredToken(response.data.data.tokens.accessToken);
      setStoredRefreshToken(response.data.data.tokens.refreshToken);
    }
    return response.data.data!;
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/register', data);
    if (response.data.data) {
      setStoredToken(response.data.data.tokens.accessToken);
      setStoredRefreshToken(response.data.data.tokens.refreshToken);
    }
    return response.data.data!;
  },

  async logout(): Promise<void> {
    const storedRefresh = getStoredRefreshToken();
    try {
      await apiClient.post('/auth/logout', storedRefresh ? { refreshToken: storedRefresh } : {});
    } finally {
      clearStoredToken();
      clearStoredRefreshToken();
    }
  },

  async getProfile(): Promise<{ user: User; tenant: Tenant }> {
    const response = await apiClient.get<ApiResponse<{ user: User; tenant: Tenant }>>('/auth/me');
    return response.data.data!;
  },

  async refreshToken(refreshToken: string): Promise<{ accessToken: string; refreshToken?: string }> {
    const response = await apiClient.post<ApiResponse<{ accessToken: string; refreshToken?: string }>>(
      '/auth/refresh',
      { refreshToken }
    );
    if (response.data.data) {
      setStoredToken(response.data.data.accessToken);
      // v3.18.10: backend now rotates the refresh token on every successful
      // /auth/refresh call. Persist the new one immediately so the next
      // refresh uses it — otherwise the second refresh hits a revoked row.
      if (response.data.data.refreshToken) {
        setStoredRefreshToken(response.data.data.refreshToken);
      }
    }
    return response.data.data!;
  },

  async completeOnboarding(): Promise<void> {
    await apiClient.patch<ApiResponse<{ onboardingComplete: boolean }>>('/auth/onboarding-complete');
  },
};

// ============================================================================
// Products API
// ============================================================================

export const productsApi = {
  async list(params?: { page?: number; limit?: number; search?: string; sort?: string; order?: string; channel?: string }): Promise<PaginatedResponse<Product>> {
    const response = await apiClient.get<PaginatedResponse<Product>>('/products', { params });
    return response.data;
  },

  async listUnmapped(params?: { page?: number; limit?: number; search?: string }): Promise<PaginatedResponse<Product>> {
    const response = await apiClient.get<PaginatedResponse<Product>>('/products/unmapped', { params });
    return response.data;
  },

  async get(id: string): Promise<Product> {
    const response = await apiClient.get<ApiResponse<Product>>(`/products/${id}`);
    return response.data.data!;
  },

  async create(data: CreateProductData): Promise<Product> {
    const response = await apiClient.post<ApiResponse<Product>>('/products', data);
    return response.data.data!;
  },

  async update(id: string, data: UpdateProductData): Promise<Product> {
    const response = await apiClient.patch<ApiResponse<Product>>(`/products/${id}`, data);
    return response.data.data!;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/products/${id}`);
  },

  async updateStock(id: string, currentStock: number, reason?: string): Promise<Product> {
    const response = await apiClient.patch<ApiResponse<Product>>(`/products/${id}/stock`, {
      currentStock,
      reason,
    });
    return response.data.data!;
  },

  async getMappings(id: string): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(`/products/${id}/mappings`);
    return response.data.data!;
  },

  async addMapping(productId: string, data: { channelId: string; externalId: string; externalSku?: string }): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(`/products/${productId}/mappings`, data);
    return response.data.data!;
  },

  async removeMapping(productId: string, mappingId: string): Promise<void> {
    await apiClient.delete(`/products/${productId}/mappings/${mappingId}`);
  },

  async bulkUpdateBufferStock(bufferStock: number): Promise<{ updated: number }> {
    const response = await apiClient.patch<ApiResponse<{ updated: number }>>('/products/bulk-buffer', { bufferStock });
    return response.data.data!;
  },
};

// ============================================================================
// Channels API
// ============================================================================

export const channelsApi = {
  async list(): Promise<Channel[]> {
    const response = await apiClient.get<ApiResponse<Channel[]>>('/channels');
    return response.data.data!;
  },

  async get(id: string): Promise<Channel> {
    const response = await apiClient.get<ApiResponse<Channel>>(`/channels/${id}`);
    return response.data.data!;
  },

  /**
   * Search the channel's external catalog (server-side Redis snapshot — at
   * most one external pull per 15 min; results annotated with alreadyMapped).
   */
  async searchCatalog(channelId: string, search: string): Promise<CatalogSearchResponse> {
    const response = await apiClient.get<ApiResponse<CatalogSearchResponse>>(
      `/channels/${channelId}/catalog`,
      { params: search ? { search } : undefined }
    );
    return response.data.data!;
  },

  async create(data: CreateChannelData): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/channels', data);
    return response.data.data!;
  },

  /**
   * Start Wix OAuth flow - returns authorization URL (Advanced OAuth)
   */
  async startWixOAuth(): Promise<{ authUrl: string; state: string }> {
    const response = await apiClient.get<ApiResponse<{ authUrl: string; state: string }>>(
      '/channels/wix/oauth-start'
    );
    return response.data.data!;
  },

  /**
   * Authenticate Wix channel using Basic OAuth (Client Credentials)
   * Used for marketplace installs where instanceId is already known
   */
  async connectWixBasicOAuth(instanceId: string): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>(
      '/channels/wix/basic-auth',
      { instanceId }
    );
    return response.data.data!;
  },

  /**
   * Start Shopify OAuth flow - returns authorization URL
   */
  async startShopifyOAuth(shop: string): Promise<{ authUrl: string; state: string }> {
    const response = await apiClient.get<ApiResponse<{ authUrl: string; state: string }>>(
      '/channels/shopify/oauth-start',
      { params: { shop } }
    );
    return response.data.data!;
  },

  /**
   * Validate WooCommerce credentials
   */
  async validateWooCommerce(data: { siteUrl: string; consumerKey: string; consumerSecret: string }): Promise<{ valid: boolean; storeName?: string }> {
    const response = await apiClient.post<ApiResponse<{ valid: boolean; storeName?: string }>>(
      '/channels/woocommerce/validate',
      data
    );
    return response.data.data!;
  },

  /**
   * Start Uber Eats OAuth flow - returns authorization URL
   */
  async startUberEatsOAuth(): Promise<{ authUrl: string; state: string }> {
    const response = await apiClient.get<ApiResponse<{ authUrl: string; state: string }>>(
      '/channels/uber-eats/oauth-start'
    );
    return response.data.data!;
  },

  async update(id: string, data: UpdateChannelData): Promise<Channel> {
    const response = await apiClient.patch<ApiResponse<Channel>>(`/channels/${id}`, data);
    return response.data.data!;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/channels/${id}`);
  },

  async testConnection(id: string): Promise<{ status: string; message: string; channelId?: string; type?: string }> {
    const response = await apiClient.post<ApiResponse<{ status: string; message: string; channelId?: string; type?: string }>>(
      `/channels/${id}/test`
    );
    return response.data.data!;
  },

  async sync(id: string): Promise<{ jobId: string; channelId: string; channelName: string; channelType: string; operation: string; status: string }> {
    const response = await apiClient.post<ApiResponse<{ jobId: string; channelId: string; channelName: string; channelType: string; operation: string; status: string }>>(`/sync/channel/${id}`);
    return response.data.data!;
  },
};

// ============================================================================
// Alerts API
// ============================================================================

export const alertsApi = {
  async list(params?: { page?: number; limit?: number; unreadOnly?: boolean }): Promise<PaginatedResponse<Alert>> {
    const response = await apiClient.get<PaginatedResponse<Alert>>('/alerts', { params });
    return response.data;
  },

  async get(id: string): Promise<Alert> {
    const response = await apiClient.get<ApiResponse<Alert>>(`/alerts/${id}`);
    return response.data.data!;
  },

  async markRead(id: string): Promise<Alert> {
    const response = await apiClient.patch<ApiResponse<Alert>>(`/alerts/${id}`, { isRead: true });
    return response.data.data!;
  },

  async markAllRead(): Promise<void> {
    await apiClient.post('/alerts/mark-all-read');
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/alerts/${id}`);
  },

  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<ApiResponse<{ unreadCount: number }>>('/alerts/unread-count');
    return response.data.data?.unreadCount ?? 0;
  },
};

// ============================================================================
// Sync API
// ============================================================================

export const syncApi = {
  async getEvents(params?: { page?: number; limit?: number; channelId?: string }): Promise<PaginatedResponse<SyncEvent>> {
    const response = await apiClient.get<PaginatedResponse<SyncEvent>>('/sync/events', { params });
    return response.data;
  },

  async triggerFullSync(): Promise<{ jobIds: string[]; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }> {
    const response = await apiClient.post<ApiResponse<{ jobIds: string[]; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }>>('/sync/full');
    return response.data.data!;
  },

  async triggerChannelSync(channelId: string): Promise<{ jobId: string; channelId: string; channelName: string; channelType: string; operation: string; status: string }> {
    const response = await apiClient.post<ApiResponse<{ jobId: string; channelId: string; channelName: string; channelType: string; operation: string; status: string }>>(`/sync/channel/${channelId}`);
    return response.data.data!;
  },

  async triggerDirectImport(channelId: string): Promise<{ imported: number; updated: number; total: number }> {
    const response = await apiClient.post<ApiResponse<{ imported: number; updated: number; total: number }>>(`/sync/channel/${channelId}/import`);
    return response.data.data!;
  },

  async triggerProductSync(productId: string): Promise<{ jobIds: string[]; productId: string; productSku: string; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }> {
    const response = await apiClient.post<ApiResponse<{ jobIds: string[]; productId: string; productSku: string; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }>>(`/sync/product/${productId}`);
    return response.data.data!;
  },

  async triggerReconciliation(): Promise<{ jobIds: string[]; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }> {
    const response = await apiClient.post<ApiResponse<{ jobIds: string[]; channelsQueued: Array<{ id: string; name: string; type: string }>; status: string }>>('/sync/full');
    return response.data.data!;
  },

  async getStatus(): Promise<SyncStatus> {
    const response = await apiClient.get<ApiResponse<SyncStatus>>('/sync/status');
    return response.data.data!;
  },
};

// ============================================================================
// Dashboard API
// ============================================================================

export const dashboardApi = {
  async getStats(): Promise<DashboardStats> {
    const response = await apiClient.get<ApiResponse<DashboardStats>>('/dashboard/stats');
    return response.data.data!;
  },

  async getRecentActivity(limit = 10): Promise<SyncEvent[]> {
    const response = await apiClient.get<ApiResponse<SyncEvent[]>>('/dashboard/activity', {
      params: { limit },
    });
    return response.data.data!;
  },

  async getAgentStatus(): Promise<EngineStatus> {
    const response = await apiClient.get<ApiResponse<EngineStatus>>('/dashboard/agents');
    return response.data.data!;
  },

  async getStockOverview(): Promise<{
    overview: {
      total: number;
      inStock: number;
      lowStock: number;
      outOfStock: number;
      totalUnits: number;
    };
    lowStockItems: Array<{
      id: string;
      sku: string;
      name: string;
      currentStock: number;
      bufferStock: number;
    }>;
  }> {
    const response = await apiClient.get<ApiResponse<any>>('/dashboard/stock-overview');
    return response.data.data!;
  },

  async getOverview(): Promise<DashboardOverview> {
    const response = await apiClient.get<ApiResponse<DashboardOverview>>('/dashboard/overview');
    return response.data.data!;
  },

  async getBootstrap(): Promise<BootstrapData> {
    const response = await apiClient.get<ApiResponse<BootstrapData>>('/dashboard/bootstrap');
    return response.data.data!;
  },
};

// ============================================================================
// Admin API
// ============================================================================

export const adminApi = {
  async getStats(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/stats');
    return response.data.data!;
  },

  async getTenants(params?: { page?: number; limit?: number }): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/tenants', { params });
    return response.data.data!;
  },

  async getTenant(id: string): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/admin/tenants/${id}`);
    return response.data.data!;
  },

  async getSyncEvents(params?: { page?: number; limit?: number; status?: string; tenantId?: string }): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/sync-events', { params });
    return response.data.data!;
  },

  async getSystemHealth(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/system-health');
    return response.data.data!;
  },

  async getEnquiries(): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>('/admin/enquiries');
    return response.data.data!;
  },

  async updateEnquiryStatus(id: string, status: string): Promise<any> {
    const response = await apiClient.patch<ApiResponse<any>>(`/admin/enquiries/${id}`, { status });
    return response.data.data!;
  },

  async updateChannelCredentials(channelId: string, credentials: Record<string, unknown>): Promise<any> {
    const response = await apiClient.patch<ApiResponse<any>>(`/admin/channels/${channelId}/credentials`, { credentials });
    return response.data.data!;
  },
};

// ============================================================================
// Founder API
// ============================================================================

export const founderApi = {
  async getDashboard(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/founder/dashboard');
    return response.data.data!;
  },

  async getTenants(params?: {
    page?: number;
    limit?: number;
    search?: string;
    health_status?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/founder/tenants', { params });
    return response.data;
  },

  async getAlerts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    unread_only?: boolean;
  }): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/admin/founder/alerts', { params });
    return response.data;
  },

  async markAlertRead(id: string): Promise<void> {
    await apiClient.post(`/admin/founder/alerts/${id}/read`);
  },

  async markAllAlertsRead(): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>('/admin/founder/alerts/read-all');
    return response.data.data!;
  },
};

// ============================================================================
// Health API
// ============================================================================

export const healthApi = {
  async check(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get<{ status: string; timestamp: string }>('/health');
    return response.data;
  },

  async detailed(): Promise<{
    status: string;
    services: {
      database: boolean;
      redis: boolean;
      queues: boolean;
    };
  }> {
    const response = await apiClient.get('/health/detailed');
    return response.data;
  },
};

// ============================================================================
// Billing API
// ============================================================================

export const billingApi = {
  async getStatus(): Promise<BillingStatus> {
    const response = await apiClient.get<ApiResponse<BillingStatus>>('/billing/status');
    return response.data.data!;
  },

  async createCheckout(plan: 'starter' | 'growth'): Promise<{ url: string }> {
    const response = await apiClient.post<ApiResponse<{ url: string }>>(
      '/billing/checkout',
      { plan }
    );
    return response.data.data!;
  },

  async openPortal(): Promise<{ url: string }> {
    const response = await apiClient.post<ApiResponse<{ url: string }>>(
      '/billing/portal'
    );
    return response.data.data!;
  },
};

// ============================================================================
// API Keys API
// ============================================================================

export interface ApiKeyData {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  callsThisMonth: number;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface ApiKeysResponse {
  plan: string;
  monthlyLimit: number | null;
  totalUsage: number;
  keys: ApiKeyData[];
}

export interface GeneratedKeyResponse {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  key: string; // plaintext, shown once
}

export const apiKeysApi = {
  async list(): Promise<ApiKeysResponse> {
    const response = await apiClient.get<ApiResponse<ApiKeysResponse>>('/settings/api-keys');
    return response.data.data!;
  },

  async generate(name: string, scopes: string[]): Promise<{ data: GeneratedKeyResponse; message: string }> {
    const response = await apiClient.post<ApiResponse<GeneratedKeyResponse> & { message?: string }>(
      '/settings/api-keys',
      { name, scopes }
    );
    return { data: response.data.data!, message: response.data.message || '' };
  },

  async revoke(id: string): Promise<void> {
    await apiClient.delete(`/settings/api-keys/${id}`);
  },

  async getUsage(id: string): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/settings/api-keys/${id}/usage`);
    return response.data.data!;
  },
};

// ============================================================================
// AI Dashboard API
// ============================================================================

export interface AIDashboardOverview {
  insightCards: {
    yesterdayRecap: { revenue: number; transactions: number; itemsSold: number };
    overnightAlerts: { count: number; products: string[] };
    deliverooRisk: { outOfStock: number; totalMapped: number } | null;
    channelRisk: { zeroStockCount: number; negativeStockCount: number };
    trending: { productName: string; units7d: number; currentStock: number; reorderDays: number | null } | null;
    dataHealth: { negativeStockCount: number };
  };
  stockOutCountdown: Array<{ name: string; sku: string; currentStock: number; velocity: number; daysLeft: number; status: 'CRITICAL' | 'WARNING' | 'OK' }>;
  channelRisk: Array<{ channelName: string; channelType: string; lastSync: string | null; outOfStock: number; lowStock: number; healthy: number; totalMapped: number; negativeStock: number }>;
  revenueLeaders: Array<{ name: string; sku: string; estimatedRevenue: number; unitsSold: number; salePrice: number }>;
  marginAnalysis: Array<{ name: string; sku: string; salePrice: number; costPrice: number; marginAbs: number; marginPct: number; totalMargin?: number; unitsSold?: number }>;
  stockCorrections: Array<{ name: string; sku: string; currentStock: number; bufferStock: number }>;
  reorderQueue: Array<{ name: string; sku: string; currentStock: number; bufferStock: number; deficit: number; estCost: number; priority: 'URGENT' | 'HIGH' | 'MEDIUM'; stockStatus: 'OUT_OF_STOCK' | 'LOW' }>;
  salesPulse: {
    ordersToday: number | null;
    revenueToday: number | null;
    itemsSoldToday: number | null;
    avgOrderValue: number | null;
    sameDayAvg: { orders: number; revenue: number; items: number; label: string } | null;
  };
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiDashboardApi = {
  async getOverview(): Promise<AIDashboardOverview> {
    const response = await apiClient.get<ApiResponse<AIDashboardOverview>>('/ai-dashboard/overview');
    return response.data.data!;
  },

  async chat(message: string, conversationHistory?: ChatMessage[]): Promise<ChatMessage> {
    const response = await apiClient.post<ApiResponse<ChatMessage>>('/ai-dashboard/chat', {
      message,
      conversationHistory,
    });
    return response.data.data!;
  },
};

// ============================================================================
// Chat Support API (AI Support Agent)
// ============================================================================

export interface SupportChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pendingAction?: boolean;
  proactive?: boolean;
  created_at: string;
}

export interface SupportConversation {
  id: string;
  surface: 'app' | 'marketing';
  status: string;
  created_at: string;
}

export const chatApi = {
  async createConversation(surface: 'app' | 'marketing'): Promise<SupportConversation> {
    const response = await apiClient.post<ApiResponse<SupportConversation>>('/chat/conversations', { surface });
    return response.data.data!;
  },

  async listConversations(): Promise<SupportConversation[]> {
    const response = await apiClient.get<ApiResponse<SupportConversation[]>>('/chat/conversations');
    return response.data.data!;
  },

  async getMessages(conversationId: string): Promise<SupportChatMessage[]> {
    const response = await apiClient.get<ApiResponse<SupportChatMessage[]>>(`/chat/conversations/${conversationId}/messages`);
    return response.data.data!;
  },

  async sendMessage(conversationId: string, content: string): Promise<SupportChatMessage> {
    const response = await apiClient.post<ApiResponse<SupportChatMessage>>(`/chat/conversations/${conversationId}/messages`, { content });
    return response.data.data!;
  },

  async submitFeedback(messageId: string, rating: 'helpful' | 'unhelpful'): Promise<void> {
    await apiClient.post<ApiResponse<void>>(`/chat/messages/${messageId}/feedback`, { rating });
  },
};

// ============================================================================
// WebSocket Client
// ============================================================================

export type WebSocketEventHandler = (event: any) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private handlers: Map<string, Set<WebSocketEventHandler>> = new Map();
  private isIntentionallyClosed = false;
  private authTimeoutHandle: NodeJS.Timeout | null = null;

  connect(): void {
    const token = getStoredToken();
    if (!token) {
      console.warn('No token available for WebSocket connection');
      return;
    }

    this.isIntentionallyClosed = false;
    const wsUrl = `${WS_BASE_URL}/ws`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected, sending auth message');
        // Send auth message as first frame
        this.sendAuthMessage(token);
        // Set timeout for auth response
        this.authTimeoutHandle = setTimeout(() => {
          console.error('[WebSocket] Auth timeout');
          if (this.ws) {
            this.ws.close(4001, 'Auth timeout');
          }
        }, 5000);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Handle auth response
          if (data.type === 'auth_success') {
            console.log('[WebSocket] Authenticated');
            if (this.authTimeoutHandle) {
              clearTimeout(this.authTimeoutHandle);
              this.authTimeoutHandle = null;
            }
            this.reconnectAttempts = 0;
            this.emit('connected', data);
          } else if (data.type === 'auth_error') {
            console.error('[WebSocket] Auth error:', data.payload?.message);
            if (this.authTimeoutHandle) {
              clearTimeout(this.authTimeoutHandle);
              this.authTimeoutHandle = null;
            }
            if (this.ws) {
              this.ws.close(4001, 'Auth error');
            }
          } else {
            this.emit(data.type, data);
          }
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        if (this.authTimeoutHandle) {
          clearTimeout(this.authTimeoutHandle);
          this.authTimeoutHandle = null;
        }
        this.emit('disconnected', { code: event.code, reason: event.reason });

        if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        if (this.authTimeoutHandle) {
          clearTimeout(this.authTimeoutHandle);
          this.authTimeoutHandle = null;
        }
        this.emit('error', { error });
      };
    } catch (error) {
      console.error('[WebSocket] Failed to connect:', error);
    }
  }

  private sendAuthMessage(token: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'auth', token }));
    }
  }

  disconnect(): void {
    this.isIntentionallyClosed = true;
    if (this.authTimeoutHandle) {
      clearTimeout(this.authTimeoutHandle);
      this.authTimeoutHandle = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (!this.isIntentionallyClosed) {
        this.connect();
      }
    }, delay);
  }

  on(event: string, handler: WebSocketEventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off(event: string, handler: WebSocketEventHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  private emit(event: string, data: any): void {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[WebSocket] Handler error for event ${event}:`, error);
      }
    });

    // Also emit to wildcard handlers
    this.handlers.get('*')?.forEach((handler) => {
      try {
        handler({ type: event, ...data });
      } catch (error) {
        console.error(`[WebSocket] Wildcard handler error:`, error);
      }
    });
  }

  send(type: string, data?: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data }));
    } else {
      console.warn('[WebSocket] Cannot send - not connected');
    }
  }

  subscribe(room: string): void {
    this.send('subscribe', { room });
  }

  unsubscribe(room: string): void {
    this.send('unsubscribe', { room });
  }

  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

// Singleton WebSocket client
export const wsClient = new WebSocketClient();

// ============================================================================
// Export Default API Client
// ============================================================================

export default apiClient;
